/**
 * Product Recommendations Carousel - Basé sur carroussel-sorties-jeuxvideos.js
 * Utilise CSS Grid + JavaScript natif (PAS Swiper.js)
 */

class ProductRecommendationsCarousel {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.swiper-wrapper');
    // Chercher d'abord les boutons personnalisés, sinon les boutons Swiper par défaut
    const section = container.closest('[id^="shopify-section-"]') || container.closest('product-recommendations') || container.closest('.product-recommendations');
    this.prevButton = section?.querySelector('.product-recommendations-nav-prev') || section?.querySelector('.swiper-button-prev');
    this.nextButton = section?.querySelector('.product-recommendations-nav-next') || section?.querySelector('.swiper-button-next');
    this.items = container.querySelectorAll('.swiper-slide');
    
    // Variables pour le swipe
    this.isDragging = false;
    this.startPos = 0;
    this.prevTranslate = 0;
    this.itemsPerView = 0;

    this.init();
  }

  init() {
    if (!this.track || this.items.length === 0) return;

    // Détruire toute instance Swiper.js créée par le custom element
    if (this.container.swiper && typeof this.container.swiper.destroy === 'function') {
      try {
        this.container.swiper.destroy(true, true);
        this.container.swiper = null;
      } catch (e) {
        console.warn('Error destroying swiper instance:', e);
      }
    }

    // Calculer le nombre d'items visibles selon la largeur d'écran
    this.updateVisibleItems();
    this.updateNavigationButtons();

    // Navigation par boutons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scrollPrev());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollNext());
    }

    // Navigation tactile et souris
    this.setupTouchEvents();

    // Réajuster lors du redimensionnement
    window.addEventListener('resize', () => {
      this.updateVisibleItems();
      this.updateNavigationButtons();
    });

    // Mise à jour des boutons lors du scroll
    this.track.addEventListener('scroll', () => {
      this.updateNavigationButtons();
    });
  }

  updateVisibleItems() {
    const containerWidth = this.track.offsetWidth;
    const itemWidth = this.items[0]?.offsetWidth || 0;
    
    if (itemWidth > 0) {
      this.itemsPerView = Math.floor(containerWidth / itemWidth);
    } else {
      // Fallback basé sur la largeur d'écran
      if (window.innerWidth <= 768) {
        this.itemsPerView = 2; // Mobile
      } else {
        this.itemsPerView = 4; // Desktop
      }
    }
  }

  updateNavigationButtons() {
    if (!this.prevButton || !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const currentScroll = this.track.scrollLeft;

    // Masquer/afficher les boutons selon la position
    if (currentScroll <= 0) {
      this.prevButton.classList.add('hidden');
    } else {
      this.prevButton.classList.remove('hidden');
    }

    if (currentScroll >= maxScroll - 5) { // -5 pour la tolérance
      this.nextButton.classList.add('hidden');
    } else {
      this.nextButton.classList.remove('hidden');
    }

    // Masquer les boutons si tous les items sont visibles
    if (this.items.length <= this.itemsPerView) {
      this.prevButton.classList.add('hidden');
      this.nextButton.classList.add('hidden');
    }
  }

  scrollPrev() {
    const itemWidth = this.items[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.track).gap) || 20;
    const scrollAmount = (itemWidth + gap) * this.itemsPerView;
    
    this.track.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
  }

  scrollNext() {
    const itemWidth = this.items[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.track).gap) || 20;
    const scrollAmount = (itemWidth + gap) * this.itemsPerView;
    
    this.track.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }

  setupTouchEvents() {
    // Touch events pour mobile
    this.track.addEventListener('touchstart', this.touchStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this.touchMove.bind(this), { passive: true });
    this.track.addEventListener('touchend', this.touchEnd.bind(this));

    // Mouse events pour desktop
    this.track.addEventListener('mousedown', this.touchStart.bind(this));
    this.track.addEventListener('mousemove', this.touchMove.bind(this));
    this.track.addEventListener('mouseup', this.touchEnd.bind(this));
    this.track.addEventListener('mouseleave', this.touchEnd.bind(this));

    // Empêcher le drag des images
    this.track.querySelectorAll('img').forEach(img => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  touchStart(event) {
    this.isDragging = true;
    this.startPos = this.getPositionX(event);
    this.prevTranslate = this.track.scrollLeft;
    this.track.style.cursor = 'grabbing';
    this.track.style.scrollBehavior = 'auto';
  }

  touchMove(event) {
    if (!this.isDragging) return;
    
    const currentPosition = this.getPositionX(event);
    const diff = this.startPos - currentPosition;
    this.track.scrollLeft = this.prevTranslate + diff;
  }

  touchEnd() {
    this.isDragging = false;
    this.track.style.cursor = 'grab';
    this.track.style.scrollBehavior = 'smooth';
  }

  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }
}

// Fonction pour initialiser un carousel
function initCarousel(container) {
  if (container && container.hasAttribute('data-no-auto-init')) {
    new ProductRecommendationsCarousel(container);
  }
}

// Initialisation au chargement du DOM
document.addEventListener('DOMContentLoaded', () => {
  // Chercher dans les sections normales
  const carousels = document.querySelectorAll('#shopify-section-main-product-recommendations swiper-slider.swiper, product-recommendations swiper-slider.swiper, .product-recommendations swiper-slider.swiper');
  
  carousels.forEach(container => {
    initCarousel(container);
  });
  
  // Observer les changements dans product-recommendations (chargement asynchrone)
  const productRecommendations = document.querySelectorAll('product-recommendations');
  productRecommendations.forEach((pr) => {
    // Vérifier si un swiper-slider existe déjà
    const existingSlider = pr.querySelector('swiper-slider.swiper');
    if (existingSlider) {
      setTimeout(() => initCarousel(existingSlider), 300);
    }
    
    // Observer les ajouts futurs
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const swiperSlider = node.querySelector ? node.querySelector('swiper-slider.swiper') : null;
            if (swiperSlider || (node.tagName === 'SWIPER-SLIDER' && node.classList.contains('swiper'))) {
              const container = swiperSlider || node;
              setTimeout(() => initCarousel(container), 500);
            }
          }
        });
      });
    });
    
    observer.observe(pr, {
      childList: true,
      subtree: true
    });
  });
});

// Support pour le theme editor de Shopify
document.addEventListener('shopify:section:load', (event) => {
  const container = event.target.querySelector('swiper-slider.swiper');
  if (container) {
    setTimeout(() => initCarousel(container), 300);
  }
});

document.addEventListener('shopify:section:select', (event) => {
  const container = event.target.querySelector('swiper-slider.swiper');
  if (container) {
    setTimeout(() => initCarousel(container), 300);
  }
});
